import {useEffect, useCallback, useContext, useState} from 'react';
import {ReadonlyContext} from '@features/contexts/ReadonlyContext';
import amplitudeService from '@features/analytics/amplitudeService';
import type {MessageAttachment} from '@features/db/messageAttachment';
import {useSendMessage} from '../hooks/useSendMessage';
import {useScreenCreationContext} from '../hooks/ScreenCreationContext';
import {useGetActiveScreenId} from '@features/activeScreenIdContext/useGetActiveScreenId';
import {trpc} from '@features/utils/trpc';
import {FlowContext} from '@features/contexts/FlowContext';
import {toast} from 'sonner';
import {captureException} from '@sentry/react';
import {
  getEffectiveGenType,
  isModernGenType,
} from '@features/server/utils/getEffectiveGenType';
import {isComponentLibraryItem} from '@features/flow/utils/componentScreenId';

function getAllInnerTexts(element: HTMLElement): string[] {
  const textArray: string[] = [];
  const treeWalker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);

  let node;
  while ((node = treeWalker.nextNode())) {
    const text = node.textContent?.trim() ?? '';
    if (text) {
      textArray.push(text);
    }
  }

  return textArray;
}

function getIconName(element: HTMLElement): string {
  // Inline SVG with data-icon attribute — new components-genType Icon.jsx
  // (both Solar via <Icon i="home" type="linear" /> and Lucide-fallback via
  // <Icon i="home" />). The Solar suffix on the value gets stripped below.
  const dataIcon = element
    .querySelector('[data-icon]')
    ?.getAttribute('data-icon');
  if (dataIcon) {
    // "home-linear" -> "home", strip Solar type suffix
    const name = dataIcon.replace(
      /-(?:linear|outline|bold|bold-duotone|broken)$/,
      '',
    );
    return name.replace(/-/g, ' ');
  }
  // <iconify-icon> element — HTML/CSS genType output, AND legacy
  // components-genType outputs rendered by main's pre-Solar Icon.jsx and
  // stored in old flowVersionsTable snapshots. Keep this branch for
  // backward compatibility — removing it breaks click-to-prototype on any
  // flow whose latest version predates the new Icon.jsx.
  const iconifyIcon = element.querySelector('iconify-icon');
  const iconAttr = iconifyIcon?.getAttribute('icon');
  if (!iconAttr) return '';
  // "lucide:heart" -> "heart", "lucide:shopping-cart" -> "shopping cart"
  const name = iconAttr.split(':').pop() ?? '';
  return name.replace(/-/g, ' ');
}

export const useBananiButtonClick = () => {
  const {readonly} = useContext(ReadonlyContext);
  const getActiveScreenId = useGetActiveScreenId();
  const {sendMessage} = useSendMessage();
  const {createScreenRelativeTo} = useScreenCreationContext();
  const {flow} = useContext(FlowContext);
  const trpcUtils = trpc.useUtils();
  const [pendingNextScreen, setPendingNextScreen] = useState<
    (MessageAttachment & {type: 'previousScreen'}) | null
  >(null);

  const isComponents = flow.genType === 'components';

  const handleConfirm = useCallback(
    async (comment: string) => {
      if (!pendingNextScreen) return;

      amplitudeService.logEvent('[Chat] generate next screen confirmed', {
        nextScreen: pendingNextScreen,
      });
      setPendingNextScreen(null);

      const sourceScreenId = pendingNextScreen.screenId;

      // For component flows, copy the source screen first (like variations),
      // then send the message to the copy so it appears on canvas immediately.
      if (isComponents) {
        try {
          const targetScreenId = await createScreenRelativeTo(
            sourceScreenId,
            'right',
            {
              sourceScreenId,
              displayNameSuffix: ' (Next)',
              filenameSuffix: '_next',
            },
          );

          await sendMessage({
            screenId: targetScreenId,
            attachments: [pendingNextScreen],
            messageText: comment,
          });

          amplitudeService.logEvent('[Chat] prototype next screen', {
            nextScreen: pendingNextScreen,
          });
        } catch (error) {
          captureException(error);
          toast.error('Failed to generate next screen');
        }
        return;
      }

      try {
        // Fetch source structure's genType to ensure correct system prompt selection.
        const sourceStructure =
          await trpcUtils.screens.getActiveStructure.fetch({
            screenId: sourceScreenId,
          });
        const sourceGenType = getEffectiveGenType(
          sourceStructure?.genType,
          null,
          flow.genType,
        );

        // Include sourceGenType in attachment for generation to use.
        const attachmentWithGenType: MessageAttachment = {
          ...pendingNextScreen,
          sourceGenType: isModernGenType(sourceGenType)
            ? sourceGenType
            : undefined,
        };

        const newScreenId = await createScreenRelativeTo(
          sourceScreenId,
          'right',
        );
        await sendMessage({
          screenId: newScreenId,
          attachments: [attachmentWithGenType],
          messageText: comment,
        });

        amplitudeService.logEvent('[Chat] prototype next screen', {
          nextScreen: pendingNextScreen,
        });
      } catch (error) {
        captureException(error);
        console.error('failed to generate next screen', error);
        toast.error('Failed to generate next screen');
      }
    },
    [
      pendingNextScreen,
      sendMessage,
      createScreenRelativeTo,
      trpcUtils,
      flow.genType,
      isComponents,
    ],
  );

  const handleCancel = useCallback(() => {
    if (!pendingNextScreen) return;

    amplitudeService.logEvent('[Chat] generate next screen cancelled', {
      nextScreen: pendingNextScreen,
    });
    setPendingNextScreen(null);
  }, [pendingNextScreen]);

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      // ShadowDivWithForwardEvents re-dispatches shadow DOM clicks on the
      // host with the original target preserved via Object.defineProperty,
      // so walking up from event.target reaches the shadow element.
      // (usePartialEdit.handleClick calls stopPropagation on the React
      // synthetic event, but the forwarded native copy still bubbles to
      // document — see the active-screen guard below.)
      let target: HTMLElement | null =
        event.target instanceof HTMLElement ? event.target : null;

      // Traverse up the DOM tree to find the nearest ancestor with the data attribute
      while (target !== null) {
        if (target.dataset.mediaType === 'banani-button') {
          const screenId =
            target
              .closest('[data-banani-screenid]')
              ?.getAttribute('data-banani-screenid') ?? 'unknown';

          // On the active screen the partial-edit click handler owns the
          // interaction, so skip generate-next for it.
          if (screenId === getActiveScreenId()) return;

          amplitudeService.logEvent('[Chat] prototype element clicked');

          // If in readonly mode, redirect to sign up
          if (readonly) {
            amplitudeService.logEvent('[Preview] try banani click from button');
            window.open('/auth/signup', '_blank');
            return;
          }

          if (isComponentLibraryItem(screenId)) return;

          const txts = getAllInnerTexts(target);
          const hasText = txts.length > 0;
          const nextScreen = hasText
            ? txts.join(', ')
            : target.dataset.mediaAction || getIconName(target);
          if (!nextScreen) return;

          amplitudeService.logEvent('[Chat] generate next screen modal shown', {
            nextScreen,
          });
          setPendingNextScreen({
            type: 'previousScreen',
            buttonHtml: target.outerHTML,
            buttonText: nextScreen,
            isIconOnly: !hasText,
            screenId,
          });
          return;
        }
        target = target.parentElement;
      }
    };

    document.addEventListener('click', handleClick);

    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, [readonly, getActiveScreenId]);

  return {
    pendingNextScreen: pendingNextScreen,
    handleConfirm,
    handleCancel,
  };
};
